import { useEffect } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useCreateBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useCart } from '@zenbusiness/application-commons-ecommerce';
import { useCreateAccount } from '@zenbusiness/application-commons-identity';
import { useCaptureException, ZBError } from '@zenbusiness/application-commons-logging';

import useFlowState from '#client/hooks/flow/useFlowState';
import useIsFormationRequired from '#client/hooks/useIsFormationRequired';
import { getBusinessNameWithIndicator } from '#client/utils/businessInfo';

import { usePaymentContext } from './usePaymentContext';

/**
 * Hook that manages the place order process.
 *
 * @param {object} params
 * @param {Function} params.onError - Function to handle errors
 * @param {Function} params.submitOrder - Function to submit the order
 * @returns {Function} Function to place an order.
 */
export function usePlaceOrder({ onError, submitOrder }) {
  const captureException = useCaptureException();
  // TODO: Is this needed or can we remove the dependency and pass the paymentDetails via placeOrder? If so, we can use this hook more generically and move to root hooks.
  const [paymentState] = usePaymentContext();
  const { businessInfo: storedBusinessInfo, get, set } = useFlowState();
  const formationRequired = useIsFormationRequired();
  const { create: createAccount, errors: accountErrors } = useCreateAccount();
  const { create: createBusiness, errors: businessErrors } = useCreateBusinessEntity();
  const { errors: cartErrors } = useCart();
  const { accountUuid: activeAccountUuid } = useAuthentication();
  const trackAccountCreated = useClickstreamCallback('accountCreated');

  const storedAccountUuid = get('accountUuid');
  const storedBusinessEntityUuid = storedBusinessInfo.businessEntityUuid;

  // Get the first error from each step
  const accountError = accountErrors[0];
  const businessError = businessErrors[0];
  const cartError = cartErrors[0];

  // Handle hook-level errors
  useEffect(() => {
    const fatalError = businessError || cartError;

    if (fatalError) {
      const { message, extensions } = fatalError || {};
      captureException(fatalError);
      onError?.({ isFatal: true, message, ...extensions });
    } else if (accountError) {
      const { message, extensions } = accountError || {};
      captureException(accountError);
      onError?.({ isFatal: false, message, ...extensions });
    }
  }, [accountError, businessError, cartError, captureException, onError]);

  // Note: This cardToken is to allow Apple pay button to pass it directly to this function.
  //       Once the Apple pay button is refactored, this might change.
  return async function placeOrder({ accountInfo, businessInfo, cardToken }) {
    let accountUuid = activeAccountUuid || storedAccountUuid || null;
    let businessEntityUuid = storedBusinessEntityUuid || null;

    try {
      // 1. Create account if not already created
      if (!accountUuid) {
        accountUuid = await createAccount(accountInfo);
        if (!accountUuid) {
          return null; // Return early if account creation fails
        }
        trackAccountCreated();
        set('accountUuid', accountUuid);
      }

      // 2. Create business entity if not already created
      if (!businessEntityUuid) {
        const businessDetails = {
          accountUuid,
          jurisdictionCode: businessInfo.jurisdictionState,
          name: getBusinessNameWithIndicator(businessInfo.name, businessInfo.companyIndicator),
          type: businessInfo.entityType,
          formationRequired
        };

        businessEntityUuid = await createBusiness(businessDetails);
        if (!businessEntityUuid) {
          return null; // Return early if business creation fails
        }
        set('businessInfo.businessEntityUuid', businessEntityUuid);
      }

      // 3. Submit order with account, business, and payment details
      const orderUuid = await submitOrder({
        accountUuid,
        businessEntityUuid,
        cardToken: cardToken || paymentState.cardToken,
        paymentMethodId: paymentState.paymentMethodId
      });

      if (!orderUuid) {
        return null;
      }

      return orderUuid;
    } catch (error) {
      // Process the error - API can return array or single error
      const processedError = Array.isArray(error) ? error[0] : error;
      const { message, extensions } = processedError || {};

      // Default to non-fatal error for order submission issues
      // This allows the user to retry their payment if it fails
      const isFatal = false;
      const errorMessage = message || 'Failed to place order';
      onError?.({ isFatal, message: errorMessage, ...extensions });

      const zbError = new ZBError(processedError, {
        contextData: {
          identifiers: {
            accountUuid,
            businessEntityUuid
          }
        }
      });
      captureException(zbError);
      return null;
    }
  };
}
